import { useEffect, useRef, useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { authClient } from "@/auth/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { LogoMark } from "@/components/Logo";
import { AuthAmbience } from "@/components/auth/AuthAmbience";
import { LoginAltMethods } from "@/components/auth/LoginAltMethods";
import { LoginOtpStep } from "@/components/auth/LoginOtpStep";
import { Envelope, ArrowRight, CircleNotch } from "@phosphor-icons/react";
import { authErrorMessage } from "@/lib/auth-error";

export const Route = createFileRoute("/login")({
  component: LoginPage,
  validateSearch: (search: Record<string, unknown>) => ({
    redirect: (
      typeof search.redirect === "string" ? search.redirect : ""
    ) as string,
  }),
});

const RESEND_COOLDOWN_SECONDS = 30;

function LoginPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { redirect: redirectTo } = Route.useSearch();
  const loginSubtitle = t("login.subtitle");

  const [otpEmail, setOtpEmail] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState("");
  const [resendCooldown, setResendCooldown] = useState(0);

  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const autoSubmittedOtpRef = useRef("");

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = window.setTimeout(
      () => setResendCooldown((s) => s - 1),
      1000,
    );
    return () => window.clearTimeout(timer);
  }, [resendCooldown]);

  async function sendOtp() {
    setError("");
    setLoading(true);
    try {
      const result = await authClient.emailOtp.sendVerificationOtp({
        email: otpEmail.trim(),
        type: "sign-in",
      });
      if (result.error) {
        setError(authErrorMessage(result.error, t, "login.otpError"));
      } else {
        setOtpSent(true);
        setResendCooldown(RESEND_COOLDOWN_SECONDS);
      }
    } catch {
      setError(t("login.genericError"));
    } finally {
      setLoading(false);
    }
  }

  async function handleSendOtp(e: React.FormEvent) {
    e.preventDefault();
    await sendOtp();
  }

  async function verifyOtp() {
    setError("");
    if (otp.length < 6) return;
    setLoading(true);
    try {
      const result = await authClient.signIn.emailOtp({
        email: otpEmail.trim(),
        otp: otp.trim(),
      });
      if (result.error) {
        setError(authErrorMessage(result.error, t, "login.otpInvalid"));
        setOtp("");
      } else {
        await queryClient.invalidateQueries({ queryKey: ["auth", "session"] });
        // Awaited so the spinner stays until the destination (behind the
        // /post-login and onboarding redirects) is on screen, instead of an
        // idle, re-submittable form while those requests run on a slow phone.
        await navigate({ to: "/post-login", search: { redirect: redirectTo || "/" } });
      }
    } catch {
      setError(t("login.genericError"));
      setOtp("");
    } finally {
      setLoading(false);
    }
  }

  async function handleVerifyOtp(e: React.FormEvent) {
    e.preventDefault();
    await verifyOtp();
  }

  useEffect(() => {
    if (otp.length < 6) {
      autoSubmittedOtpRef.current = "";
      return;
    }
    if (!otpSent || loading || autoSubmittedOtpRef.current === otp) return;
    autoSubmittedOtpRef.current = otp;
    void verifyOtp();
  }, [otp, otpSent, loading]);

  async function handlePasswordLogin(email: string, password: string) {
    setError("");
    setLoading(true);
    try {
      const result = await authClient.signIn.email({ email: email.trim(), password });
      if (result.error) {
        // Suspendovan račun ili previše pokušaja imaju svoju poruku; sve
        // ostalo je "provjeri email i lozinku".
        setError(
          authErrorMessage(result.error, t, "login.wrongCredentials", {
            INVALID_EMAIL_OR_PASSWORD: "login.wrongCredentials",
            INVALID_EMAIL: "login.wrongCredentials",
            INVALID_PASSWORD: "login.wrongCredentials",
            PASSWORD_TOO_SHORT: "login.wrongCredentials",
            PASSWORD_TOO_LONG: "login.wrongCredentials",
          }),
        );
      } else {
        await queryClient.invalidateQueries({ queryKey: ["auth", "session"] });
        await navigate({ to: "/post-login", search: { redirect: redirectTo || "/" } });
      }
    } catch {
      setError(t("login.genericError"));
    } finally {
      setLoading(false);
    }
  }

  async function handleSocialLogin(provider: "google") {
    setError("");
    try {
      await authClient.signIn.social({
        provider,
        callbackURL: `/post-login?redirect=${encodeURIComponent(redirectTo || "/")}`,
      });
    } catch {
      setError(t("login.socialError"));
    }
  }

  return (
    <div className="relative flex min-h-svh flex-col bg-background">
      <AuthAmbience />

      {/* Centered composition */}
      <main className="relative z-10 flex flex-1 flex-col items-center justify-center px-5 py-12 sm:px-8">
        <div className="w-full max-w-[27rem]">
          <div className="animate-ink-rise text-center">
            <Link
              to="/"
              className="inline-flex flex-col items-center gap-2 transition-opacity hover:opacity-75"
            >
              <LogoMark aria-hidden className="size-9 sm:size-10" />
              <span className="text-sm font-semibold tracking-tight text-foreground">
                Psihotron
              </span>
            </Link>
            <h1
              className="mt-5 text-balance text-[1.9rem] font-extrabold leading-[1.08] tracking-[-0.02em] text-foreground sm:text-[2.35rem]"
              style={{ fontFamily: "var(--font-heading)" }}
            >
              {t("login.brandTitle")}{" "}
              <span style={{ color: "var(--color-brand)" }}>
                {t("login.brandHighlight")}
              </span>
            </h1>
            <p className="mx-auto mt-3 max-w-sm text-sm leading-relaxed text-muted-foreground">
              {loginSubtitle || t("login.brandSubtitle")}
            </p>
          </div>

          {/* Glass card */}
          <div
            className="animate-ink-rise mt-8 rounded-[1.75rem] border border-white/70 bg-white/65 p-6 shadow-[0_24px_70px_-32px_rgba(25,43,48,0.45)] backdrop-blur-xl sm:p-8"
            style={{ animationDelay: "0.12s" }}
          >
            {error && (
              <div
                role="alert"
                className="mb-5 rounded-2xl border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive"
              >
                {error}
              </div>
            )}

            {otpSent ? (
              <LoginOtpStep
                key="otp-verify"
                email={otpEmail}
                otp={otp}
                onOtpChange={(value) => {
                  setOtp(value);
                  setError("");
                }}
                invalid={!!error}
                loading={loading}
                resendCooldown={resendCooldown}
                onSubmit={handleVerifyOtp}
                onResend={() => void sendOtp()}
                onChangeEmail={() => {
                  setOtpSent(false);
                  setOtpEmail("");
                  setOtp("");
                  setError("");
                }}
              />
            ) : (
              <form
                key="otp-send"
                onSubmit={handleSendOtp}
                className="flex flex-col gap-3"
              >
                <Label htmlFor="otp-email">{t("login.emailLabel")}</Label>
                <div className="relative">
                  <Envelope className="absolute left-4 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    id="otp-email"
                    type="email"
                    placeholder={t("login.emailPlaceholder")}
                    value={otpEmail}
                    onChange={(e) => setOtpEmail(e.target.value)}
                    required
                    autoComplete="email"
                    className="h-12 rounded-2xl bg-white pl-11"
                  />
                </div>
                <Button
                  type="submit"
                  size="lg"
                  disabled={loading || !otpEmail.trim()}
                  className="h-12 w-full rounded-full text-sm font-semibold shadow-[0_10px_24px_-10px_var(--color-brand-shadow)] transition-all hover:-translate-y-0.5 hover:shadow-[0_14px_28px_-10px_var(--color-brand-shadow-hover)]"
                >
                  {loading ? (
                    <CircleNotch className="animate-spin" data-icon="inline-start" />
                  ) : null}
                  {t("login.continueEmail")}
                  {!loading && <ArrowRight data-icon="inline-end" />}
                </Button>
                <p className="text-center text-xs text-muted-foreground">
                  {t("login.otpHint")}
                </p>
              </form>
            )}

            {!otpSent && (
              <LoginAltMethods
                loading={loading}
                onGoogle={() => void handleSocialLogin("google")}
                onPasswordSubmit={(email, password) =>
                  void handlePasswordLogin(email, password)
                }
                onClearError={() => setError("")}
              />
            )}
          </div>

          {/* Footer */}
          <p
            className="animate-ink-rise mt-8 text-center text-xs text-muted-foreground/70"
            style={{ animationDelay: "0.24s" }}
          >
            {t("footer.copyright", { year: new Date().getFullYear() })}
          </p>
        </div>
      </main>
    </div>
  );
}
